/**
 * Hour and minute for the picked day, as two columns of chips
 * (`poc:.pf-picker__body`).
 *
 * Two columns rather than a clock or a text field because the answer is coarse:
 * shoots start on the quarter hour, so there are four minute values and the
 * column IS the whole vocabulary. A free-text time field would accept 10:07 and
 * then have to reject it.
 *
 * The window is 07:00–21:00, which is the prototype's own bound — no slot starts
 * outside it (`poc:"SHOOTING DAY"`). Hours are written the way the language writes
 * them: "8 am" on the 12-hour clock of English, "08" or "8" on the 24-hour clock
 * of every other language.
 */

import React, { ReactNode } from 'react'
import { Stack, Typography } from '@mui/material'
import moment from 'moment-timezone'
import { useTranslation } from 'react-i18next'

import { BLUE_200, GRAY_300, GRAY_700, SELECTED_BLUE } from 'constants/styling/theme'
import { MUIDivider } from 'components/common/MUIDivider'
import { SelectableButtonsGrid } from 'components/common/SelectableButtonsGrid'

import { MINUTE_STEPS, SHOOTING_HOURS } from './shootingDateTime'

const pad = (value: number) => String(value).padStart(2, '0')

const CHIP_SX = {
  '&&&': { height: '4rem', py: 0, borderColor: GRAY_300, fontSize: '1.6rem', fontWeight: 450 },
  '&&&:not(:disabled):hover, &&&[aria-checked="true"]': { backgroundColor: BLUE_200, borderColor: SELECTED_BLUE },
}

/** The hour in the language's own time format, with a muted am/pm when that format is a 12-hour clock. */
export const hourLabel = (hour: number, language: string): ReactNode => {
  const time = moment({ hour }).locale(language)
  const format = time.localeData().longDateFormat('LT')
  const hourToken = format.match(/H+|h+/)?.[0] ?? 'HH'
  if (!/a/i.test(format)) return time.format(hourToken)

  return (
    <>
      {time.format(hourToken)}
      <Typography component="span" variant="text-xs" fontWeight={450} color={GRAY_700} ml={0.4}>{time.format('a')}</Typography>
    </>
  )
}

interface Props {
  /** Hour of day, or undefined while nothing is picked. */
  hour?: number
  /** Minute past the hour, one of {@link MINUTE_STEPS}. */
  minute?: number
  onPickHour: (hour: number) => void
  onPickMinute: (minute: number) => void
  /** Nothing can be picked, e.g. while no day is chosen. */
  disabled?: boolean
  isHourDisabled?: (hour: number) => boolean
  isMinuteDisabled?: (minute: number) => boolean
}

export const ShootingTimePicker: React.FC<Props> = ({
  hour,
  minute,
  onPickHour,
  onPickMinute,
  disabled = false,
  isHourDisabled,
  isMinuteDisabled,
}) => {
  const { t, i18n } = useTranslation(['purchase_flow_revamp'])
  const picked = hour !== undefined && minute !== undefined ? `${pad(hour)}:${pad(minute)}` : undefined

  return (
    // Beside the calendar the columns fill its height and scroll within it; they are taken out of the flow so the
    // fifteen hours never stretch the panel. Stacked on a phone there is no calendar height to match, so they cap.
    <Stack data-cy="pf-revamp-shooting-time-picker" sx={{ position: 'relative', flex: 1, minHeight: { md: 0 } }}>
      <Stack
        direction="row"
        gap={1.6}
        divider={<MUIDivider orientation="vertical" flexItem margin={0} />}
        sx={{ maxHeight: { xs: '32rem', md: 'none' }, position: { md: 'absolute' }, inset: { md: 0 } }}
      >

        <Stack flex={1} sx={{ overflowY: 'auto', pr: 0.4 }}>
          <SelectableButtonsGrid
            direction="column"
            selectionRole="radio"
            ariaLabel={t('purchase_flow_revamp:instructions.schedule.hour_label')}
            values={SHOOTING_HOURS.map((value) => ({ value, displayName: hourLabel(value, i18n.language), disabled: disabled || !!isHourDisabled?.(value), dataCy: `pf-revamp-schedule-hour-${value}` }))}
            selectedValue={hour}
            onSelect={onPickHour}
            gap={0.8}
            sx={CHIP_SX}
          />
        </Stack>

        <Stack flex={1} sx={{ overflowY: 'auto', pr: 0.4 }}>
          <SelectableButtonsGrid
            direction="column"
            selectionRole="radio"
            ariaLabel={t('purchase_flow_revamp:instructions.schedule.minute_label')}
            values={MINUTE_STEPS.map((value) => ({ value, displayName: pad(value), disabled: disabled || !!isMinuteDisabled?.(value), dataCy: `pf-revamp-schedule-minute-${value}` }))}
            selectedValue={minute}
            onSelect={onPickMinute}
            gap={0.8}
            sx={CHIP_SX}
          />
        </Stack>

      </Stack>

    </Stack>
  )
}
